import { defineComponent as _defineComponent } from 'vue'
import Formatter from '@/assets/js/utils/Formatter';
import WidgetErrorMessage from '@/components/widgets/components/WidgetErrorMessage.vue';
import WidgetLoading from '@/components/widgets/components/WidgetLoading.vue';

import type { WidgetConfig } from '@/modules/ctx-dashboard';
import { WidgetType } from '@/modules/ctx-dashboard';
import { WidgetUtils, WidgetComposable, WidgetSettings } from '@/components/widgets';
import TimeIntervals from '@/assets/js/utils/TimeIntervals';
import { WidgetError } from '@/components/widgets/types/WidgetError';
import { ref, onMounted, nextTick, computed, watch } from 'vue';
import { MenuAction } from '@/components/widgets/types/MenuAction';

type Props = {
    widgetConfig: WidgetConfig;
    report: boolean;
    expand: boolean;
    enabledDataStreaming: boolean;
}
type Emits = {
    (e: 'loaded', component: any): void;
    (e: 'action', action: string): void;
    (e: 'update:dataintegrity', dataIntegrity: boolean): void;
    (e: 'update:info', info: string): void;
    (e: 'update:subtitle', subtitle: string): void;
    (e: 'update:actions', actions: MenuAction[]): void;
    (e: 'update:settings', widget: WidgetSettings): void;
    (e: 'error', error: WidgetError|null): void;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'WidgetLoader',
  props: {
    widgetConfig: null,
    report: { type: Boolean },
    expand: { type: Boolean },
    enabledDataStreaming: { type: Boolean }
  },
  emits: ["loaded", "action", "update:dataintegrity", "update:info", "update:subtitle", "update:actions", "update:settings", "error"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
    (e: 'loaded', component: any): void;
    (e: 'action', action: string): void;
    (e: 'update:dataintegrity', dataIntegrity: boolean): void;
    (e: 'update:info', info: string): void;
    (e: 'update:subtitle', subtitle: string): void;
    (e: 'update:actions', actions: MenuAction[]): void;
    (e: 'update:settings', widget: WidgetSettings): void;
    (e: 'error', error: WidgetError|null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    widgetConfig: WidgetConfig;
    report: boolean;
    expand: boolean;
    enabledDataStreaming: boolean;
};



expose({
    getWidget,
    getMenuActions,
});

const refWidget = ref<WidgetComposable | null>(null);
const error = ref<WidgetError|null>(null);
const loading = ref<boolean>(true);
const renderWidgetComponent = ref<boolean>(false);
const subtitle = ref<string>('');
const title = computed<string>(() => props.widgetConfig.title
    || WidgetUtils.getDefaultWidgetTitle(props.widgetConfig)
    || props.widgetConfig.generatedTitle);

const widgetComponent = computed<any>(() => {
    switch (props.widgetConfig.type) {
        case WidgetType.ScatterChart: return () => import(/* webpackChunkName: "widgets" */'./scatter/WidgetChartScatter.vue');
        case WidgetType.StackedTimeChart:
        case WidgetType.TimeChart: return () => import(/* webpackChunkName: "widgets" */'./timechart/WidgetTimeChart.vue');
        case WidgetType.StackedGeneratorChart:
        case WidgetType.GeneratorChart: return () => import(/* webpackChunkName: "widgets" */'./generatorchart/WidgetChartGenerator.vue');
        case WidgetType.Gauge:
        case WidgetType.Number: return () => import(/* webpackChunkName: "widgets" */'./number/WidgetNumber.vue');
        case WidgetType.Distribution: return () => import(/* webpackChunkName: "widgets" */'./distribution/WidgetDistribution.vue');
        case WidgetType.Logs: return () => import(/* webpackChunkName: "widgets" */'./logs/WidgetLogs.vue');
        case WidgetType.Availability:
        case WidgetType.Commissions:
        case WidgetType.Table: return () => import(/* webpackChunkName: "widgets" */'./table/WidgetTable.vue');
        case WidgetType.Map: return () => import(/* webpackChunkName: "widgets" */'./map/WidgetMap.vue');
        case WidgetType.Windrose: return () => import(/* webpackChunkName: "widgets" */'./windrose/WidgetWindrose.vue');
        case WidgetType.WeatherMap: return () => import(/* webpackChunkName: "widgets" */'./map-weather/WidgetWeatherMap.vue');
        case WidgetType.Forecast: return () => import(/* webpackChunkName: "widgets" */'./forecast/WidgetChartForecast.vue');
        case WidgetType.Heatmap: return () => import(/* webpackChunkName: "widgets" */'./heatmap/WidgetChartHeatmap.vue');
        case WidgetType.PowerCurve: return () => import(/* webpackChunkName: "widgets" */'./powercurve/WidgetChartPowercurve.vue');
        case WidgetType.Note: return () => import(/* webpackChunkName: "widgets" */'./note/WidgetNote.vue');
        case WidgetType.LatestLogs: return () => import(/* webpackChunkName: "widgets" */'./latest-logs/WidgetLatestLogs.vue');
        case WidgetType.OperatorForecast: return () => import(/* webpackChunkName: "widgets" */'./operator-forecast/WidgetOperatorForecast.vue');
        default:
    }
    return () => import('./WidgetInvalid.vue');
});

function getWidget(): WidgetComposable | null {
    return refWidget.value as WidgetComposable | null;
}
function getMenuActions(): MenuAction[] {
    return refWidget.value?.getMenuActions() || [];
}
function updateSubtitle(newSubtitle?: string): void {
    if (newSubtitle !== undefined) {
        subtitle.value = newSubtitle;
    } else if (props.widgetConfig.intervalName && props.widgetConfig.intervalName !== 'newest') {
        const interval = TimeIntervals.resolveIntervalForWidget(props.widgetConfig);
        const exclusive = TimeIntervals.hasExclusiveEndDate(props.widgetConfig.intervalName);
        subtitle.value = Formatter.formatTimeInterval(interval.from, interval.to, false, exclusive);
    }
}
function initialized() {
    if (!loading.value) {
        // emit event in nextTick after vue finished rendering
        nextTick(() => emits('loaded', refWidget.value));
    }
}
function onError() {
    emits('error', error.value);
}

watch(() => loading.value, initialized);
watch(() => error.value, onError);

onMounted(() => {
    updateSubtitle();
    nextTick(() => renderWidgetComponent.value = true);
});

return { __sfc: true,props, emits, refWidget, error, loading, renderWidgetComponent, subtitle, title, widgetComponent, getWidget, getMenuActions, updateSubtitle, initialized, onError, WidgetErrorMessage, WidgetLoading }
}

})